"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ApiClient = void 0;
const axios_1 = __importDefault(require("axios"));
const uuid_1 = require("uuid");
const contract_1 = require("../types/contract");
const paginated_1 = require("../types/paginated");
const akord_auth_1 = require("@akord/akord-auth");
const unauthorized_1 = require("../errors/unauthorized");
const error_factory_1 = require("../errors/error-factory");
const bad_request_1 = require("../errors/bad-request");
const not_found_1 = require("../errors/not-found");
const cross_fetch_1 = __importDefault(require("cross-fetch"));
const crypto_1 = require("@akord/crypto");
const CONTENT_RANGE_HEADER = 'Content-Range';
const CONTENT_LOCATION_HEADER = 'Content-Location';
const GATEWAY_HEADER_PREFIX = 'x-amz-meta-';
class ApiClient {
    constructor() {
        // API endpoints
        this._fileUri = "files";
        this._contractUri = "contracts";
        this._transactionUri = "transactions";
        this._vaultUri = "vaults";
        this._nodeUri = "nodes";
        this._membershipUri = "memberships";
        this._userUri = "users";
        this._queryParams = {};
        this.addQueryParams = function (url, params) {
            const queryParams = new URLSearchParams(JSON.parse(JSON.stringify(params)));
            url += "?" + queryParams.toString();
            return url;
        };
    }
    clone() {
        const clone = new ApiClient();
        clone._gatewayurl = this._gatewayurl;
        clone._apiurl = this._apiurl;
        clone._resourceId = this._resourceId;
        clone._vaultId = this._vaultId;
        clone._tags = this._tags;
        clone._state = this._state;
        clone._input = this._input;
        clone._metadata = this._metadata;
        clone._numberOfChunks = this._numberOfChunks;
        clone._data = this._data;
        clone._queryParams = this._queryParams;
        clone._progressId = this._progressId;
        clone._progressHook = this._progressHook;
        clone._cancelHook = this._cancelHook;
        clone._isPublic = this._isPublic;
        clone._totalBytes = this._totalBytes;
        clone._uploadedBytes = this._uploadedBytes;
        clone._storage = this._storage;
        return clone;
    }
    env(config) {
        this._apiurl = config.apiurl;
        this._gatewayurl = config.gatewayurl;
        return this;
    }
    resourceId(resourceId) {
        this._resourceId = resourceId;
        return this;
    }
    public(isPublic) {
        this._isPublic = isPublic;
        return this;
    }
    cloud(cloud) {
        this.queryParams({ cloud: cloud });
        return this;
    }
    storage(storage) {
        this._storage = storage;
        return this;
    }
    vaultId(vaultId) {
        this._vaultId = vaultId;
        return this;
    }
    data(data) {
        this._data = data;
        return this;
    }
    metadata(metadata) {
        this._metadata = metadata;
        if (metadata === null || metadata === void 0 ? void 0 : metadata.cloud) {
            this.cloud(metadata.cloud);
        }
        return this;
    }
    state(state) {
        this._state = state;
        return this;
    }
    queryParams(queryParams) {
        if (queryParams) {
            this._queryParams = Object.assign(Object.assign({}, this._queryParams), queryParams);
        }
        else {
            this._queryParams = {};
        }
        return this;
    }
    tags(tags) {
        this._tags = tags;
        return this;
    }
    totalBytes(totalBytes) {
        this._totalBytes = totalBytes;
        return this;
    }
    loadedBytes(uploadedBytes) {
        this._uploadedBytes = uploadedBytes;
        return this;
    }
    progressHook(hook, id) {
        this._progressHook = hook;
        this._progressId = id || (0, uuid_1.v4)();
        return this;
    }
    cancelHook(hook) {
        this._cancelHook = hook;
        return this;
    }
    input(input) {
        this._input = input;
        return this;
    }
    numberOfChunks(numberOfChunks) {
        this._numberOfChunks = numberOfChunks;
        return this;
    }
    /**
     * Initialize a vault smart contract
     * @uses:
     * - tags()
     * - state()
     * @returns {Promise<string>}
     */
    contract() {
        return __awaiter(this, void 0, void 0, function* () {
            this.data({ tags: this._tags, state: this._state });
            const response = yield this.post(`${this._apiurl}/${this._vaultUri}`);
            return response.id;
        });
    }
    /**
     * Get current vault contract state
     * @requires:
     * - vaultId()
     * @returns {Promise<Contract>}
     */
    getContract() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._vaultId) {
                throw new bad_request_1.BadRequest("Missing vault id to get contract state. Use ApiClient#vaultId() to add it");
            }
            return yield this.public(true).get(`${this._gatewayurl}/${this._contractUri}/${this._vaultId}`);
        });
    }
    /**
     *
     * @uses:
     * - queryParams() - email
     * @returns {Promise<Boolean>}
     */
    existsUser() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.get(`${this._apiurl}/${this._userUri}`);
            }
            catch (e) {
                if (!(e instanceof not_found_1.NotFound)) {
                    throw e;
                }
                return false;
            }
            return true;
        });
    }
    /**
     * Fetch currently authenticated user
     * @returns {Promise<User>}
     */
    getUser() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.get(`${this._apiurl}/${this._userUri}`);
        });
    }
    /**
     *
     * @uses:
     * - queryParams() - email
     * @returns {Promise<UserPublicInfo>}
     */
    getUserPublicData() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.get(`${this._apiurl}/${this._userUri}`);
        });
    }
    /**
     *
     * @uses:
     * - data()
     */
    updateUser() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.fetch("put", `${this._apiurl}/${this._userUri}`);
        });
    }
    /**
     *
     * @uses:
     * - vaultId()
     */
    deleteVault() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.delete(`${this._apiurl}/${this._vaultUri}/${this._vaultId}`);
        });
    }
    /**
     *
     * @uses:
     * - vaultId()
     * @returns {Promise<Array<Membership>>}
     */
    getMembers() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.get(`${this._apiurl}/${this._vaultUri}/${this._vaultId}/members`);
        });
    }
    /**
     * Get memberships for currently authenticated user
     * @uses:
     * - queryParams() - limit, nextToken
     * @returns {Promise<Paginated<Membership>>}
     */
    getMemberships() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.get(`${this._apiurl}/${this._membershipUri}`);
        });
    }
    /**
     * Get vaults for currently authenticated user
     * @uses:
     * - queryParams() - limit, nextToken, tags, filter
     * @returns {Promise<Paginated<Vault>>}
     */
    getVaults() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.get(`${this._apiurl}/${this._vaultUri}`);
        });
    }
    /**
     * Get user membership keys for given vault
     * @uses:
     * - vaultId()
     * @returns {Promise<MembershipKeys>}
     */
    getMembershipKeys() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.public(true).get(`${this._apiurl}/${this._vaultUri}/${this._vaultId}/keys`);
        });
    }
    /**
     * Get nodes by vault id and type
     * @uses:
     * - vaultId()
     * - queryParams() - type, parentId, limit, nextToken, tags, filter
     * @returns {Promise<Paginated<T>>}
     */
    getNodesByVaultId() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.public(true).get(`${this._apiurl}/${this._vaultUri}/${this._vaultId}/${this._nodeUri}`);
        });
    }
    /**
     * Get memberships by vault id
     * @uses:
     * - vaultId()
     * - queryParams() - limit, nextToken, filter
     * @returns {Promise<Paginated<Membership>>}
     */
    getMembershipsByVaultId() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.get(`${this._apiurl}/${this._vaultUri}/${this._vaultId}/${this._membershipUri}`);
        });
    }
    /**
     * Get node by id and type
     * @uses:
     * - resourceId()
     * - queryParams() - type
     * @returns {Promise<T>}
     */
    getNode() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.public(true).get(`${this._apiurl}/${this._nodeUri}/${this._resourceId}`);
        });
    }
    /**
     * Get membership by id
     * @uses:
     * - resourceId()
     * @returns {Promise<Membership>}
     */
    getMembership() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.get(`${this._apiurl}/${this._membershipUri}/${this._resourceId}`);
        });
    }
    /**
     * Get vault by id
     * @uses:
     * - resourceId()
     * - queryParams() - withNodes, withMemberships, withMemos, withStacks, withFolders
     * @returns {Promise<Vault>}
     */
    getVault() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.public(true).get(`${this._apiurl}/${this._vaultUri}/${this._resourceId}`);
        });
    }
    /**
     * Get transactions by vault id
     * @uses:
     * - vaultId()
     * @returns {Promise<Array<Transaction>>}
     */
    getTransactions() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.get(`${this._apiurl}/${this._vaultUri}/${this._vaultId}/${this._transactionUri}`);
        });
    }
    getTransactionTags() {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const response = yield (0, axios_1.default)({
                    method: 'head',
                    url: `${this._apiurl}/files/${this._resourceId}`
                });
                return Object.keys(response.headers)
                    .filter(header => header.startsWith(GATEWAY_HEADER_PREFIX))
                    .map(header => {
                    return new contract_1.Tag(header.replace(GATEWAY_HEADER_PREFIX, ''), response.headers[header]);
                });
            }
            catch (error) {
                (0, error_factory_1.throwError)((_a = error.response) === null || _a === void 0 ? void 0 : _a.status, (_c = (_b = error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.msg, error);
            }
        });
    }
    invite() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.post(`${this._apiurl}/${this._vaultUri}/${this._vaultId}/members`);
            return response.id;
        });
    }
    inviteResend() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.post(`${this._apiurl}/${this._vaultUri}/${this._vaultId}/members/${this._resourceId}`);
            return response.id;
        });
    }
    revokeInvite() {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield this.delete(`${this._apiurl}/${this._vaultUri}/${this._vaultId}/members/${this._resourceId}`);
            return response.id;
        });
    }
    post(url) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.fetch("post", url);
        });
    }
    patch(url) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.fetch("patch", url);
        });
    }
    get(url) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.fetch("get", url);
        });
    }
    delete(url) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.fetch("delete", url);
        });
    }
    fetch(method, url) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const auth = yield akord_auth_1.Auth.getAuthorization();
            if (!auth && !this._isPublic) {
                throw new unauthorized_1.Unauthorized("Authentication is required to use Akord API");
            }
            const config = {
                method,
                url: this._queryParams ? this.addQueryParams(url, this._queryParams) : url,
                headers: {
                    'Authorization': auth,
                    'Content-Type': 'application/json'
                }
            };
            if (this._data) {
                config.data = this._data;
            }
            try {
                const response = yield (0, axios_1.default)(config);
                if ((0, paginated_1.isPaginated)(response)) {
                    return { items: response.data, nextToken: (0, paginated_1.nextToken)(response) };
                }
                return response.data;
            }
            catch (error) {
                (0, error_factory_1.throwError)((_a = error.response) === null || _a === void 0 ? void 0 : _a.status, (_c = (_b = error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.msg, error);
            }
        });
    }
    /**
     *
     * @requires:
     * - vaultId()
     * - input()
     * - tags()
     * @uses:
     * - metadata()
     * @returns {Promise<{ id: string, object: T }>}
     */
    transaction() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._vaultId) {
                throw new bad_request_1.BadRequest("Missing vault id to post transaction. Use ApiClient#vaultId() to add it");
            }
            if (!this._input) {
                throw new bad_request_1.BadRequest("Missing input to post transaction. Use ApiClient#input() to add it");
            }
            if (!this._tags) {
                throw new bad_request_1.BadRequest("Missing tags to post transaction. Use ApiClient#tags() to add it");
            }
            this.data({
                input: this._input,
                tags: this._tags,
                metadata: this._metadata
            });
            const { id, object } = yield this.post(`${this._apiurl}/${this._vaultUri}/${this._vaultId}/${this._transactionUri}`);
            return { id, object };
        });
    }
    /**
     * Schedules transaction posting
     * @requires:
     * - resourceId()
     * @uses:
     * - tags()
     * - public()
     * - numberOfChunks()
     */
    asyncTransaction() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._resourceId) {
                throw new bad_request_1.BadRequest("Missing resource id to schedule transaction posting. Use ApiClient#resourceId() to add it");
            }
            this.data({
                resourceUrl: this._resourceId,
                tags: this._tags,
                async: true,
                numberOfChunks: this._numberOfChunks
            });
            yield this.post(`${this._apiurl}/${this._transactionUri}/${this._fileUri}`);
        });
    }
    /**
     *
     * @requires:
     * - state()
     * @uses:
     * - tags()
     * - cloud()
     * @returns {Promise<string>}
     */
    uploadState() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._state) {
                throw new bad_request_1.BadRequest("Missing state to upload. Use ApiClient#state() to add it");
            }
            this.data({ data: this._state, tags: this._tags });
            const response = yield this.post(`${this._apiurl}/states`);
            return response.id;
        });
    }
    /**
     *
     * @requires:
     * - data()
     * @uses:
     * - resourceId()
     * - tags()
     * - storage()
     * - public()
     * - progressHook()
     * - cancelHook()
     * @returns {Promise<string[]>}
     */
    uploadFile() {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const auth = yield akord_auth_1.Auth.getAuthorization();
            if (!auth) {
                throw new unauthorized_1.Unauthorized("Authentication is required to use Akord API");
            }
            if (!this._data) {
                throw new bad_request_1.BadRequest("Missing data to upload. Use ApiClient#data() to add it");
            }
            const me = this;
            const headers = {
                'Authorization': auth,
                'Tags': (0, crypto_1.jsonToBase64)(this._tags),
                'Storage-Class': (_a = this._storage) === null || _a === void 0 ? void 0 : _a.replace(":", ""),
                'Content-Type': 'application/octet-stream'
            };
            if (this._numberOfChunks > 1) {
                headers[CONTENT_RANGE_HEADER] = `bytes ${this._uploadedBytes}-${this._uploadedBytes + this._data.byteLength}/${this._totalBytes}`;
            }
            if (this._resourceId) {
                headers[CONTENT_LOCATION_HEADER] = this._resourceId;
            }
            this._progressId = (0, uuid_1.v4)();
            const config = {
                method: 'post',
                url: `${this._apiurl}/files`,
                data: this._data,
                headers: headers,
                signal: this._cancelHook ? this._cancelHook.signal : null,
                onUploadProgress(progressEvent) {
                    if (me._progressHook) {
                        let percentageProgress;
                        let bytesProgress;
                        if (me._totalBytes) {
                            bytesProgress = progressEvent.loaded;
                            percentageProgress = Math.round(bytesProgress / me._totalBytes * 100);
                        }
                        else {
                            bytesProgress = progressEvent.loaded;
                            percentageProgress = Math.round(bytesProgress / progressEvent.total * 100);
                        }
                        me._progressHook(percentageProgress, bytesProgress, me._progressId);
                    }
                }
            };
            try {
                const response = yield (0, axios_1.default)(config);
                return {
                    resourceUri: response.data.resourceUri,
                    resourceLocation: response.headers[CONTENT_LOCATION_HEADER.toLocaleLowerCase()],
                    resourceSize: this._data.byteLength
                };
            }
            catch (error) {
                (0, error_factory_1.throwError)((_b = error.response) === null || _b === void 0 ? void 0 : _b.status, (_d = (_c = error.response) === null || _c === void 0 ? void 0 : _c.data) === null || _d === void 0 ? void 0 : _d.msg, error);
            }
        });
    }
    getUploadState() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._resourceId) {
                throw new bad_request_1.BadRequest("Missing resource id to download. Use ApiClient#resourceId() to add it");
            }
            const data = yield this.get(`${this._apiurl}/files/uploader/${this._resourceId}`);
            return {
                resourceUri: data.resourceUri
            };
        });
    }
    /**
     *
     * @requires:
     * - resourceId()
     */
    downloadState() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._resourceId) {
                throw new bad_request_1.BadRequest("Missing resource id to download. Use ApiClient#resourceId() to add it");
            }
            return yield this.get(`${this._apiurl}/states/${this._resourceId}`);
        });
    }
    /**
     *
     * @requires:
     * - resourceId()
     * @uses:
     * - responseType()
     * - public()
     * - progressHook()
     * - cancelHook()
     * - numberOfChunks()
     */
    downloadFile() {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const auth = yield akord_auth_1.Auth.getAuthorization();
            if (!auth && !this._isPublic) {
                throw new unauthorized_1.Unauthorized("Authentication is required to use Akord API");
            }
            if (!this._resourceId) {
                throw new bad_request_1.BadRequest("Missing resource id to download. Use ApiClient#resourceId() to add it");
            }
            const config = {
                method: 'get',
                signal: this._cancelHook ? this._cancelHook.signal : null,
            };
            if (!this._isPublic) {
                config.headers = {
                    'Authorization': auth,
                };
            }
            try {
                const response = yield (0, cross_fetch_1.default)(`${this._apiurl}/files/${this._resourceId}`, config);
                return { resourceUrl: this._resourceId, response: response };
            }
            catch (error) {
                (0, error_factory_1.throwError)((_a = error.response) === null || _a === void 0 ? void 0 : _a.status, (_c = (_b = error.response) === null || _b === void 0 ? void 0 : _b.data) === null || _c === void 0 ? void 0 : _c.msg, error);
            }
        });
    }
}
exports.ApiClient = ApiClient;
